import { cn, IconName, NoddiIcon } from 'noddi-ui-common';
import { NoddiProgressBar } from 'noddi-ui-web';

type AttributeType = 'noise' | 'other';

type Props = {
  label: string;
  icon: IconName;
  value: string | number;
  attributeType?: AttributeType;
};

const getProgressValue = (value: string | number) => {
  if (typeof value === 'number') {
    // Handle numeric noise values (80db to 64db)
    // Clamp value between 80 and 64
    const clampedValue = Math.min(80, Math.max(64, value));
    // Calculate progress percentage (80=0%, 64=100%)
    return 100 - ((clampedValue - 64) / 16) * 100;
  } else {
    switch (value) {
      case 'A':
        return 100;
      case 'B':
        return 83.33;
      case 'C':
        return 66.67;
      case 'D':
        return 50;
      case 'E':
        return 33.33;
      case 'F':
        return 16.67;
      default:
        return 0;
    }
  }
};

const GRADE_COLOR_MAP = {
  A: 'bg-[#008000]',
  B: 'bg-[#66CC00]',
  C: 'bg-[#CCCC00]',
  D: 'bg-[#FFD700]',
  E: 'bg-[#FF9900]',
  F: 'bg-[#FF3300]',
  G: 'bg-[#CC0000]'
};

const NOISE_THRESHOLDS = [
  { max: 64, color: GRADE_COLOR_MAP.A },
  { max: 67, color: GRADE_COLOR_MAP.B },
  { max: 70, color: GRADE_COLOR_MAP.C },
  { max: 74, color: GRADE_COLOR_MAP.D },
  { max: 76, color: GRADE_COLOR_MAP.E },
  { max: 78, color: GRADE_COLOR_MAP.F },
  { max: Infinity, color: GRADE_COLOR_MAP.G }
];

const getProgressColor = (value: string | number): string => {
  if (typeof value === 'number') {
    const threshold = NOISE_THRESHOLDS.find((t) => value <= t.max);
    return threshold ? threshold.color : GRADE_COLOR_MAP.G;
  }

  return GRADE_COLOR_MAP[value as keyof typeof GRADE_COLOR_MAP] || GRADE_COLOR_MAP.G;
};

export const TireAttributeWithProgressBar = ({ label, icon, value, attributeType = 'other' }: Props) => (
  <div className='flex justify-between gap-4'>
    <div className='flex gap-2'>
      <NoddiIcon name={icon} />
      <span>{label}</span>
    </div>
    <div>
      <div className='-mb-1.5 mr-5.5 flex justify-between'>
        {attributeType === 'noise' ? <span className='text-2'>80db</span> : <span className='text-2'>G</span>}
        {attributeType === 'noise' ? <span className='text-2'>64db</span> : <span className='text-2'>A</span>}
      </div>
      <div className='flex h-fit items-center gap-2'>
        <NoddiProgressBar
          className={`h-1.5 ${getProgressColor(value)}`}
          value={getProgressValue(value)}
          wrapperClassName='border-black/40'
        />
        <span className={cn(attributeType === 'noise' && '-mr-2')}>{value}</span>
      </div>
    </div>
  </div>
);
